import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useFitRecords } from './useFitRecords';
import * as FitRecords from './fitRecordStore';
import '../styles/Dashboard.css';

// ────────────────────────────────────────────────────────────────────────
// SYSTEMS — the "Systems" entry in Madhura's mockup (client item #19).
// Route to register (App.js): <Route path="/systems" element={<SystemsPage />} />
//
// This is deliberately a STRUCTURAL first pass, per item #19's own closing
// line ("build the structure first, not the final content; Madhura will
// then review and edit") — one list, grouped the same way Run History
// groups runs, with the one action that was clearly useful without further
// design input: renaming a system. It reuses renameReactionSystemByUuid
// added to fitRecordStore.js for this page.
//
// This also gives a direct answer to "why are there so many Reaction
// System 88 / 13 / 2 entries" (raised earlier): now there's one place to
// see and rename all of them, instead of only meeting them one at a time
// inside Run History.
// ────────────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '\u2014' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const SystemsPage = () => {
  const navigate = useNavigate();
  const state = useFitRecords();
  const { allFits, allFitsScope, loading, error } = state;

  const [query, setQuery] = useState('');
  const [editingUuid, setEditingUuid] = useState(null);
  const [draft, setDraft] = useState('');
  const [savingUuid, setSavingUuid] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('role')) { navigate('/'); return; }
    FitRecords.bootstrapFitRecords();
  }, [navigate]);

  // One row per reaction system: name, uuid, run count, most recent run.
  const systems = useMemo(() => {
    const byUuid = new Map();
    allFits.forEach((f) => {
      const uuid = f.system_uuid || f.reaction_system_id || f.system_name;
      if (!uuid) return;
      if (!byUuid.has(uuid)) {
        byUuid.set(uuid, { uuid, name: f.system_name || 'Reaction system', runCount: 0, exampleCount: 0, lastRunAt: null });
      }
      const row = byUuid.get(uuid);
      row.runCount += 1;
      if (f.is_example) row.exampleCount += 1;
      if (!row.lastRunAt || new Date(f.created_at || 0) > new Date(row.lastRunAt)) row.lastRunAt = f.created_at;
    });
    let rows = [...byUuid.values()].sort((a, b) => new Date(b.lastRunAt || 0) - new Date(a.lastRunAt || 0));
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    return rows;
  }, [allFits, query]);

  const startRename = (row) => { setEditingUuid(row.uuid); setDraft(row.name); };
  const cancelRename = () => { setEditingUuid(null); setDraft(''); };

  const saveRename = async (row) => {
    const name = draft.trim();
    if (!name || name === row.name) { cancelRename(); return; }
    setSavingUuid(row.uuid);
    const ok = await FitRecords.renameReactionSystemByUuid(row.uuid, name);
    setSavingUuid(null);
    if (ok) cancelRename();
    // On failure, leave the field open with the attempted text so the user
    // can retry rather than silently losing what they typed.
  };

  return (
    <div style={styles.shell}>
      <Sidebar />
      <div style={styles.rightCol}>
        <Navbar activePage="app" />
        <div style={styles.pageContent}>
          <div className="dash-page">

            <div className="rh-head">
              <div>
                <h1 className="rh-title">Systems</h1>
                <p className="rh-sub">
                  {systems.length} reaction system{systems.length === 1 ? '' : 's'}
                  {allFitsScope !== 'all' && ' \u2014 current system only'}
                </p>
              </div>
              <input
                className="rh-search"
                type="search"
                placeholder="Search systems\u2026"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {allFitsScope !== 'all' && systems.length > 0 && (
              <p className="rh-note">Showing the current reaction system only \u2014 the backend doesn't yet support listing every system across accounts.</p>
            )}
            {error && <p className="dash-summary-error">Couldn't load systems: {error}</p>}
            {loading && systems.length === 0 && <p className="dash-summary-text">Loading\u2026</p>}
            {!loading && !error && systems.length === 0 && (
              <div className="dash-summary-card dash-summary-card--empty">
                <i className="ti ti-flask dash-summary-icon" aria-hidden="true" />
                <p className="dash-summary-text">No reaction systems yet. They're created automatically the first time you run a fit.</p>
                <button className="dash-summary-cta" onClick={() => navigate('/data-fitting')}>Run First System</button>
              </div>
            )}

            {systems.length > 0 && (
              <div className="rh-group">
                <div className="rh-row rh-row--head" aria-hidden="true">
                  <span />
                  <span>System</span>
                  <span>Examples</span>
                  <span>Last run</span>
                  <span>Runs</span>
                  <span />
                  <span>Actions</span>
                </div>
                {systems.map((row) => (
                  <div key={row.uuid} className="rh-row-wrap">
                    <div className="rh-row">
                      <i className="ti ti-flask rh-group-icon" aria-hidden="true" />
                      {editingUuid === row.uuid ? (
                        <input
                          className="rh-search sys-rename-input"
                          value={draft}
                          autoFocus
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveRename(row); if (e.key === 'Escape') cancelRename(); }}
                        />
                      ) : (
                        <span className="rh-name"><span className="rh-name-text">{row.name}</span></span>
                      )}
                      <span className="rh-meta">{row.exampleCount > 0 ? `${row.exampleCount} example${row.exampleCount === 1 ? '' : 's'}` : '\u2014'}</span>
                      <span className="rh-meta rh-date">{fmtDate(row.lastRunAt)}</span>
                      <span className="dash-count">{row.runCount}</span>
                      <span />
                      <span className="rh-actions">
                        {editingUuid === row.uuid ? (
                          <>
                            <button className="rh-action" title="Save" disabled={savingUuid === row.uuid} onClick={() => saveRename(row)}>
                              <i className="ti ti-check" aria-hidden="true" />
                            </button>
                            <button className="rh-action" title="Cancel" onClick={cancelRename}>
                              <i className="ti ti-x" aria-hidden="true" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="rh-action" title="Rename" onClick={() => startRename(row)}>
                              <i className="ti ti-pencil" aria-hidden="true" />
                            </button>
                            <button className="rh-action" title="View runs" onClick={() => navigate('/run-history')}>
                              <i className="ti ti-folder-open" aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  shell: { display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' },
  rightCol: {
    marginLeft: '220px', width: 'calc(100% - 220px)', display: 'flex',
    flexDirection: 'column', height: '100vh', overflow: 'hidden',
  },
  pageContent: { flex: 1, marginTop: '68px', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 },
};

export default SystemsPage;
